import { DEFAULT_CHECKOUT_BUTTONS_SELECTOR } from "../site-config/selectors";
import { appendSkipShopPay, enableSkipShopPay } from "../utils/skip-shop-pay";

// Storefront-wide interception that makes theme/cart-app entries into Shopify
// checkout carry `skip_shop_pay=true` (see `utils/skip-shop-pay.ts` for what the
// parameter does). Redo-owned navigations (Checkout+ buttons, rescue panel, Shop
// Pay buy-now) consult the shared flag directly; this module covers everything
// the theme/cart app does on its own:
//
// 1. Checkout links (`<a href="/checkout">`, `/checkouts/...`, cart permalinks)
//    — rewritten in capture-phase pointerdown/click handlers right before the
//    browser follows them. Rewriting the href (instead of preventDefault +
//    manual navigation) preserves cmd/ctrl-click, middle-click via auxclick, and
//    any theme click handlers.
// 2. Forms posting directly to `/checkout` — the action attribute is rewritten
//    in a capture-phase submit handler.
// 3. Checkout buttons (`DEFAULT_CHECKOUT_BUTTONS_SELECTOR`: the standard
//    `[name="checkout"]` cart-form button plus cart-app buttons like
//    Rebuy/ajax-cart/upcart that navigate in their own JS click handler). Many
//    have no URL or form action to rewrite, so we take the click over: cancel
//    whatever the button's handler would do and navigate to checkout with the
//    parameter ourselves.
//
// The takeover in (3) deliberately owns *every* checkout button, including ones
// a coverage `checkoutSelector` also targets. Coverage can't apply the parameter
// to a JS-nav button anyway — its `ensureProduct` re-click just re-triggers the
// cart app's own (param-less) navigation — and on these merchants the coverage
// product is already in the cart (added via the cart/checkout toggle) by the
// time checkout is clicked, so navigating straight to checkout loses nothing.
// This mirrors the per-merchant custom JS that this replaces. (An earlier
// version excluded coverage-owned buttons and relied on the coverage re-click to
// add the param; that left toggle-based Rebuy merchants with no param at all,
// because that re-click path is dormant when the product is already in the cart.)
//
// Residual gap: a cart app whose button is not in
// `DEFAULT_CHECKOUT_BUTTONS_SELECTOR` and that navigates via its own
// `location.href` still can't be intercepted here — add its selector to that
// list, or fall back to per-merchant `storefront-custom-js`.

let installed = false;

export function installSkipShopPayInterception(): void {
  if (installed) {
    return;
  }
  installed = true;
  enableSkipShopPay();

  // pointerdown rewrites before any mousedown-triggered navigation and
  // covers middle-click; click is the catch-all (keyboard activation
  // included) right before the default navigation runs.
  document.addEventListener("pointerdown", rewriteCheckoutAnchor, true);
  document.addEventListener("click", rewriteCheckoutAnchor, true);
  document.addEventListener("click", takeOverCheckoutButton, true);
  document.addEventListener("submit", handleFormSubmit, true);
}

// (3) Take over a checkout button and navigate with the parameter ourselves.
function takeOverCheckoutButton(event: Event): void {
  for (const target of event.composedPath()) {
    if (
      !(target instanceof Element) ||
      !target.matches(DEFAULT_CHECKOUT_BUTTONS_SELECTOR)
    ) {
      continue;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    location.href = appendSkipShopPay(`${location.origin}/checkout`);
    return;
  }
}

function rewriteCheckoutAnchor(event: Event): void {
  // composedPath (rather than event.target) so links inside open shadow
  // roots — cart-drawer apps — are still found.
  for (const target of event.composedPath()) {
    if (target instanceof HTMLAnchorElement && isCheckoutUrl(target.href)) {
      target.href = appendSkipShopPay(target.href);
      return;
    }
  }
}

function handleFormSubmit(event: SubmitEvent): void {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) {
    return;
  }
  // A submitter-level formaction overrides the form's action.
  const action =
    event.submitter?.getAttribute("formaction") ?? form.getAttribute("action");
  if (!action) {
    return;
  }
  let actionUrl: URL;
  try {
    actionUrl = new URL(action, location.href);
  } catch {
    return;
  }
  if (actionUrl.origin !== location.origin) {
    return;
  }
  if (isCheckoutUrl(actionUrl.toString())) {
    form.action = appendSkipShopPay(actionUrl.toString());
  }
}

// Same-origin URLs that enter Shopify checkout: /checkout itself,
// /checkouts/<token>, and cart permalinks (/cart/c/<token>,
// /cart/<variant>:<qty>) which Shopify resolves straight into checkout.
export function isCheckoutUrl(href: string): boolean {
  let url: URL;
  try {
    url = new URL(href, location.origin);
  } catch {
    return false;
  }
  if (url.origin !== location.origin) {
    return false;
  }
  const path = url.pathname;
  return (
    path === "/checkout" ||
    path.startsWith("/checkout/") ||
    path.startsWith("/checkouts/") ||
    path.startsWith("/cart/c/") ||
    /^\/cart\/\d+:/.test(path)
  );
}
